import React, { useState, useEffect } from 'react';
import { ModalWrapper, ButtonGroup} from 'components/molecules';
import { PopupProps } from 'react-popup-manager/dist/src';
import { Form, Button, Input, Popconfirm } from 'antd';
import { useB2cDetail } from 'hooks';
import { LecturePlace, Pm, LectureSubject, DatePicker, LectureWeek } from 'components/atoms'
import { RangeValue } from 'rc-picker/lib/interface';
import { dateFormat } from 'utils/date-utils';
import moment, { Moment } from 'moment';
import { getCodeNumberArray } from 'utils/form-utils';

export interface FileInfoType {
    fileid: number, 
    filename: string    
}

export interface B2cEditModalProps extends PopupProps {
    id: number;
}

const SeminarEditModal = ({ 
    isOpen, 
    onClose, 
    id}: B2cEditModalProps): React.ReactElement => {
  /* ======================================================
   * states
   * ====================================================== */    
    const [closeYn, setCloseYn] = useState<string>('N');  
  /* ======================================================
  * hooks
  * ====================================================== */
 const onIsClose = (isOk) => {
    if (isOk) setCloseYn(closeYn==='Y'?'N':'Y')
 }
  const { b2cInfo, insertSuccess, updateSuccess, deleteSuccess, closeSuccess, dropSuccess, b2cInsert, b2cUpdate, b2cClose, b2cDelete, b2cDrop } = useB2cDetail(id, onIsClose);
   /* ======================================================
   * variables
   * ====================================================== */
  const [form] = Form.useForm();
  const { TextArea } = Input; 


   /* ======================================================
   * events
   * ====================================================== */
    const onCancel = () => {
        onClose?.(closeSuccess);
    };

    const onSubmit = (e) => {
        if (id > 0) {
            b2cUpdate({
                id: id,
                title: e.title,
                lecture_gid: 9999,
                lecture_subject1: e.lecture_subject1,
                lecture_subject_memo: e.lecture_subject_memo,
                start_date: dateFormat(form.getFieldValue('project_date'), 'YYYY-MM-DD'),
                end_date: dateFormat(form.getFieldValue('project_date'), 'YYYY-MM-DD'),         
                lecture_place_type: e.lecture_place_type,
                lecture_studio: e.lecture_studio,
                location: e.location,            
                pm: e.pm,
                memo: e.memo,
                repeat_week: e.repeat_week,                
                week_mon: Number(e.week_mon), 
                week_tue: Number(e.week_tue),
                week_wed: Number(e.week_wed), 
                week_thu: Number(e.week_thu), 
                week_fri: Number(e.week_fri),                 
            })  
        } else {
            b2cInsert({
                id: id,
                title: e.title,
                lecture_gid: 9999,
                lecture_subject1: e.lecture_subject1,
                lecture_subject_memo: e.lecture_subject_memo,
                start_date: dateFormat(form.getFieldValue('project_date'), 'YYYY-MM-DD'),
                end_date: dateFormat(form.getFieldValue('project_date'), 'YYYY-MM-DD'), 
                lecture_place_type: e.lecture_place_type,           
                lecture_studio: e.lecture_studio,
                location: e.location,              
                pm: e.pm,
                memo: e.memo,   
                repeat_week: e.repeat_week,                
                week_mon: Number(e.week_mon?e.week_mon:0), 
                week_tue: Number(e.week_tue?e.week_tue:0),
                week_wed: Number(e.week_wed?e.week_wed:0), 
                week_thu: Number(e.week_thu?e.week_thu:0), 
                week_fri: Number(e.week_fri?e.week_fri:0),                     
            })  
        }
    };

  /* ======================================================
   * effects
   * ====================================================== */
    useEffect(() => {
        if (!!b2cInfo) {                     
            form.setFieldsValue({
                title: b2cInfo.title, 
                lecture_subject1: b2cInfo.lecture_subject1,
                lecture_subject_memo: b2cInfo.lecture_subject_memo,
                project_date: (!!b2cInfo.start_date && b2cInfo.start_date !== '0000-00-00') ? moment(b2cInfo.start_date, "YYYY-MM-DD") : undefined,
                lecture_place_type: b2cInfo.lecture_place_type,
                lecture_studio: b2cInfo.lecture_studio,
                location: b2cInfo.location,     
                pm: getCodeNumberArray(b2cInfo.pm),
                memo: b2cInfo.memo || '',
                repeat_week: b2cInfo.repeat_week || 0,
                week_mon: b2cInfo.week_mon, 
                week_tue: b2cInfo.week_tue,
                week_wed: b2cInfo.week_wed, 
                week_thu: b2cInfo.week_thu, 
                week_fri: b2cInfo.week_fri,                
            });
            setCloseYn(b2cInfo.close_yn);
        }
    }, [b2cInfo])


    useEffect(() => {
        if (insertSuccess || updateSuccess || deleteSuccess || dropSuccess) {
            onClose?.(true);  
        }
    }, [insertSuccess, updateSuccess, deleteSuccess, dropSuccess])

    return (
    <>
        <ModalWrapper open={isOpen} onCancel={onCancel} width={650} title={id===0?'세미나 등록':'세미나 수정'} closable>
            <Form
                form={form}
                name="B2cInfo"
                layout="horizontal"  
                labelCol={{ flex: '120px' }}
                onFinish={onSubmit}
                style={{width:'100%'}}
                autoComplete="off"
            >
                <Form.Item name="title" label={'세미나명'} rules={[{ required: true, message: '' }]}>
                 <Input type="text" placeholder='세미나명 입력하세요'/> 
                </Form.Item> 
                <Form.Item label={'강의 주제'}>
                    <LectureSubject form={form} />
                </Form.Item>  
                <Form.Item 
                    name="project_date" 
                    label={'교육 일자'}
                    rules={[{ required: true, message: '' }]}
                >
                    <DatePicker 
                        showTime={false} 
                        format="YYYY-MM-DD" 
                    />                    
                </Form.Item>  
                <Form.Item label={'교육 일정'}>
                    <LectureWeek form={form} />
                </Form.Item>                                                
                <Form.Item label={'세미나 장소'}>
                    <LecturePlace required={false} form={form} lecture_place_type={(id>0)?b2cInfo?.lecture_place_type||'OFFLINE':'ONLINE'} lecture_studio={b2cInfo?.lecture_studio || ''} />
                </Form.Item> 
                <Form.Item label={'PM'}>
                    <Pm fieldname={'pm'} pm={b2cInfo?.pm} />   
                </Form.Item>  
                <Form.Item name='memo' label={'메모'}>
                    <TextArea rows={3} placeholder='메모를 입력하세요'/>   
                </Form.Item>     
                {(id>0) && (
                <Form.Item label={'모집중/마감'}>
                    <Popconfirm
                        title={closeYn==='Y'?'모집중 상태로 변경 하시겠습니까?':'마감 상태로 변경 하시겠습니까?'} 
                        okText={closeYn==='Y'?'모집중':'마감'} 
                        cancelText="취소"
                        onConfirm={() => { b2cClose({id:id, close_yn:closeYn==='Y'?'N':'Y'})}}
                    >   
                        <Button>{closeYn==='Y'?'마감':'모집중'}</Button>
                    </Popconfirm>
                </Form.Item>                                 
                )}      
                <ButtonGroup className={'buttonsWrapper'}>
                    <div>
                        <Button onClick={onCancel} style={{marginRight: "5px"}}>닫기</Button>
                    </div>   
                    <div>
                        {(id>0 && !!b2cInfo && b2cInfo.bill_price === 0 && b2cInfo.status_type === 'AV') && (
                            <>
                            <Popconfirm
                                title="삭제 하시겠습니까?"
                                okText="삭제"
                                cancelText="취소"
                                onConfirm={() => { b2cDelete({id:id})}}
                            >   
                                <Button danger style={{marginRight: "5px"}}>삭제</Button>        
                            </Popconfirm>
                            <Popconfirm
                                title="폐강 하시겠습니까? 폐강 처리시 스케줄은 삭제됩니다."
                                okText="폐강"
                                cancelText="취소"
                                onConfirm={() => { b2cDrop({id:id})}}
                            >   
                                <Button danger style={{marginRight: "5px"}}>폐강</Button>        
                            </Popconfirm>                            
                            </>
                        )}
                        <Button htmlType="submit"  type="primary">{id===0?'등록':'수정'}</Button>
                    </div>
                </ButtonGroup>                
            </Form>            
        </ModalWrapper>  
    </>
    )    
};

export default React.memo(SeminarEditModal);    